/** MSDS-1 — SDS requirement triage on materials. */

export const MFG_MSDS_STATUS_VALUES = ["not_required", "undecided", "required"] as const;

export type MfgMsdsStatus = (typeof MFG_MSDS_STATUS_VALUES)[number];

export function isMfgMsdsStatus(value: unknown): value is MfgMsdsStatus {
  return typeof value === "string" && (MFG_MSDS_STATUS_VALUES as readonly string[]).includes(value);
}

export function parseMfgMsdsStatus(value: unknown, fallback: MfgMsdsStatus = "undecided"): MfgMsdsStatus {
  return isMfgMsdsStatus(value) ? value : fallback;
}

type ChipArgs = {
  status: MfgMsdsStatus;
  publishedVersion?: number | null;
  /** Draft/supplier not published (MSDS-5). */
  hasDraft?: boolean;
  hasSupplierUpload?: boolean;
};

/** Inventory chip label (§5.4). */
export function mfgMsdsStatusChipLabel(
  statusOrArgs: MfgMsdsStatus | ChipArgs,
  publishedVersion?: number | null
): string {
  const args: ChipArgs =
    typeof statusOrArgs === "string"
      ? { status: statusOrArgs, publishedVersion }
      : statusOrArgs;
  const { status } = args;
  if (status === "not_required") return "SDS n/a";
  if (status === "undecided") return "SDS undecided";
  const ver = Number(args.publishedVersion) || 0;
  if (ver >= 1) return "SDS published";
  if (args.hasSupplierUpload) return "SDS uploaded";
  if (args.hasDraft) return "SDS draft";
  return "SDS missing";
}

export function mfgMsdsStatusChipClass(
  statusOrArgs: MfgMsdsStatus | ChipArgs,
  publishedVersion?: number | null
): string {
  const args: ChipArgs =
    typeof statusOrArgs === "string"
      ? { status: statusOrArgs, publishedVersion }
      : statusOrArgs;
  const { status } = args;
  if (status === "not_required") {
    return "bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-zinc-200";
  }
  if (status === "undecided") {
    return "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100";
  }
  const ver = Number(args.publishedVersion) || 0;
  if (ver >= 1) {
    return "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100";
  }
  if (args.hasSupplierUpload) {
    return "bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100";
  }
  if (args.hasDraft) {
    return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100";
  }
  return "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100";
}
